import cx from "classnames";
import { type FC, useMemo, useState } from "react";

import { AvatarSize, AvatarWithLabel } from "../Avatar";
import { Button, type ButtonSize } from "../Button";
import { EmojiDropdown } from "../EmojiDropdown";
import { BasicTooltip } from "../Tooltip";

export type EmojiReactionUser = {
	name: string;
	id: string;
	profilePicture: string;
};

export type EmojiReactionBucket = {
	emojiName: string;
	users: EmojiReactionUser[];
};

export type EmojiReaction = { user: EmojiReactionUser; emoji: string; emojiName: string };

export interface EmojiReactionsProps {
	withPortal?: boolean;
	reactionsValue?: EmojiReaction[];
	onReactionsChange?: (emojiReactions: EmojiReaction[]) => void;
	currentUser?: EmojiReactionUser;
	readonly?: boolean;
	max?: number;
	size?: ButtonSize;
}
export const EmojiReactions: FC<EmojiReactionsProps> = ({
	currentUser,
	withPortal,
	onReactionsChange,
	reactionsValue,
	readonly,
	max,
	size = "md",
}) => {
	// Hooks
	const bucketedReactions = useMemo(() => {
		const bucketedReactions: Record<string, EmojiReactionBucket> = {};
		reactionsValue?.forEach((reaction) => {
			const bucket = bucketedReactions[reaction.emoji] || {
				emojiName: reaction.emojiName,
				users: [],
			};
			bucket.users.push(reaction.user);
			bucketedReactions[reaction.emoji] = bucket;
		});

		return bucketedReactions;
	}, [reactionsValue]);
	const [showMore, setShowMore] = useState(false);

	// Functions
	const handleReactionClick = (emoji: string, emojiName: string) => {
		// TODO handle visual readonly state
		if (readonly || currentUser === undefined) {
			return;
		}

		const userReactionIndex = reactionsValue?.findIndex(
			(r) => r.user.id === currentUser?.id && r.emoji === emoji
		);

		// Remove the reaction if the user has already reacted with that emoji
		if (userReactionIndex !== undefined && userReactionIndex >= 0) {
			const newReactions = [...(reactionsValue || [])];
			newReactions.splice(userReactionIndex, 1);
			onReactionsChange?.(newReactions);
		} else {
			// Otherwise add it
			onReactionsChange?.([
				...(reactionsValue || []),
				{
					user: currentUser,
					emoji,
					emojiName,
				},
			]);

			if (
				max !== undefined &&
				reactionsValue !== undefined &&
				reactionsValue.length + 1 > max
			) {
				setShowMore(true);
			}
		}
	};

	return (
		<div className="flex flex-wrap items-center gap-1">
			{Object.entries(bucketedReactions)
				.slice(0, max !== undefined && showMore ? undefined : max)
				.map(([emoji, { emojiName, users }]) => {
					const currentUserReacted = users.some((user) => user.id === currentUser?.id);
					return (
						<BasicTooltip
							key={emoji}
							content={
								<div>
									<div className="mb-2 flex justify-center gap-1 border-b border-primary-solid px-3 pb-2 text-xs font-bold">
										{emojiName}
									</div>

									<div className="flex flex-col gap-4 px-1 py-2">
										{users
											.sort((a) => (a.id === currentUser?.id ? -1 : 1))
											.map((user) => (
												<AvatarWithLabel
													key={user.id}
													userName={user.name}
													imageURL={user.profilePicture}
													label={user.name}
													size={AvatarSize.sm}
												/>
											))}
									</div>
								</div>
							}
						>
							<Button
								size={size}
								className={cx(
									"flex gap-1 text-sm",
									currentUserReacted && "hover:!bg-light-spectrum-blue-hover",
									currentUserReacted &&
										"focus:hover:!bg-light-spectrum-blue-focus",
									currentUserReacted && "!bg-light-spectrum-blue"
								)}
								onClick={() => handleReactionClick(emoji, emojiName)}
							>
								<div>{emoji}</div>
								<div>{users.length}</div>
							</Button>
						</BasicTooltip>
					);
				})}
			{max !== undefined && Object.values(bucketedReactions).length > max && (
				<Button size={size} onClick={() => setShowMore((p) => !p)}>
					Show {showMore ? "Less" : "More"}
				</Button>
			)}

			<BasicTooltip content="Add Reaction">
				<EmojiDropdown size={size} withPortal={withPortal} onSelect={handleReactionClick} />
			</BasicTooltip>
		</div>
	);
};
